import { RequestCooldownService } from '../../core/services/request-cooldown.service';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { switchMap } from 'rxjs';
import { FaDuotoneIconComponent, FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faClock, faKey, faPlus, faTrashCan } from '@fortawesome/pro-duotone-svg-icons';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  PrivilegesApi,
  privilegesApiSchema,
  RoleWithPassword,
} from '../../shared/schemas/backend-api';
import { defaultPrivileges } from '../../shared/constants/privileges-local';
import { TextInput } from '../../shared/components/form-fields/text-input/text-input';
import { PasswordConfirmInputs } from '../../shared/components/form-fields/password-confirm-inputs/password-confirm-inputs';
import { Button } from '../../shared/components/ui/buttons/gradient-btn/button';
import { TooltipDirective } from '../../shared/components/ui/tooltip/tooltip.directive';
import { recurrenceLabel } from '../../shared/helpers/schedule-label';
import { RoomSchedule } from '../../shared/models/schedule';
import { AppConfigStore } from '../../state/app-config.repository';
import { RoomPasswordStore } from '../../state/room-passwords.repository';
import { deriveRoomPasswordProof } from '../../shared/utils/room-password-proof';
import { RoomsStore } from '../../state/rooms.repository';
import { UserSettingsStore } from '../../state/user-settings.repository';
import { AppService } from '../../core/services/app.service';
import { AppConfigService, SetupRoomPayload } from '../../core/services/app-config.service';
import { LanguageService } from '../../core/services/language.service';
import { formatDate } from '../../shared/i18n/date-format';
import { applyThemePreset, RegionSelect, THEME_PRESETS, ThemeEditor } from '../settings';
import { RoomDialog, RoomDialogData, RoomDialogResult } from '../rooms';

const SCHEDULE_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  dateStyle: 'medium',
  timeStyle: 'short',
};

type DraftRoom = {
  tempId: string;
  name: string;
  isPrivate: boolean;
  password: string | null;
  schedule?: RoomSchedule;
};

function initialGuestPrivileges(): PrivilegesApi {
  const privileges = privilegesApiSchema.parse({});
  for (const privilege of defaultPrivileges) privileges[privilege.id] = privilege.active;
  return privileges;
}

/** One-page first-run setup. All edits remain drafts until the atomic setup call. */
@Component({
  selector: 'andor-setup-wizard',
  standalone: true,
  imports: [
    TranslatePipe,
    TextInput,
    PasswordConfirmInputs,
    Button,
    FaIconComponent,
    FaDuotoneIconComponent,
    TooltipDirective,
    RegionSelect,
    ThemeEditor,
  ],
  templateUrl: './setup-wizard.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './setup-wizard.scss',
})
export class SetupWizard {
  readonly cooldown = inject(RequestCooldownService);
  protected readonly faPlus = faPlus;
  protected readonly faTrashCan = faTrashCan;
  protected readonly faKey = faKey;
  protected readonly faClock = faClock;

  private readonly _dialog = inject(MatDialog);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _translate = inject(TranslateService);
  private readonly _locale = inject(LanguageService).locale;
  private readonly _roomPasswords = inject(RoomPasswordStore);
  private readonly _rooms = inject(RoomsStore);
  private readonly _appConfigStore = inject(AppConfigStore);
  private readonly _appService = inject(AppService);
  private readonly _appConfigService = inject(AppConfigService);
  private readonly userSettings = inject(UserSettingsStore);
  private readonly _region = viewChild.required(RegionSelect);
  private _submitted = false;

  readonly appTitle = new FormControl('Rooms', {
    nonNullable: true,
    validators: [Validators.maxLength(64)],
  });
  private readonly _appTitleEvents = toSignal(this.appTitle.events);
  readonly titleInvalid = computed(() => {
    this._appTitleEvents();
    return this.appTitle.invalid;
  });
  /** Starts empty: the owner must choose a region explicitly. */
  readonly gatewayHost = signal<string | null>(null);
  adminPassword = '';
  readonly adminPasswordValid = signal(false);
  readonly draftRooms = signal<DraftRoom[]>([
    {
      tempId: crypto.randomUUID(),
      name: this._translate.instant('Lobby'),
      isPrivate: false,
      // New rooms are end-to-end encrypted by default, the seeded one too.
      password: this._roomPasswords.generate(),
    },
  ]);
  readonly canFinish = computed(
    () =>
      !this.titleInvalid() &&
      this.adminPasswordValid() &&
      this.draftRooms().length > 0 &&
      this.draftRooms().length <= 50 &&
      this._region().ready()
  );
  readonly loading = signal(false);
  readonly errors = signal<string[]>([]);
  protected readonly logo = toSignal(
    this.userSettings.colorMode$.pipe(
      switchMap((mode) => this._appConfigStore.getAsset$(`${mode ?? 'dark'}Logo`))
    )
  );

  constructor() {
    this._appConfigStore.update({ title: 'Rooms' });
    applyThemePreset(this._appConfigStore, THEME_PRESETS[0]);
    const colorMode = this.userSettings.colorMode ?? 'dark';
    this._appConfigStore.update({ colorMode });
    this.userSettings.update({ colorMode });
    this.appTitle.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      this._appConfigStore.update({ title: value.trim() || 'Rooms' });
    });
  }

  selectRegion(host: string | null) {
    this.gatewayHost.set(host);
    if (host) this._appConfigStore.update({ gateway: host });
  }

  onPasswordEnter(event: Event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    event.preventDefault();
    void this.finalize();
  }

  // Opens the shared room dialog in create mode and, on confirm, appends the
  // result to the local list. Reuses the exact dialog from the room-login
  // screen — the dialog only builds a form result (it does not create the room
  // server-side), and `resolveCreatePassword()` returns the e2e cipher without
  // persisting it, which is what we need while the room has no server id yet.
  addRoom() {
    if (this.loading() || this.draftRooms().length >= 50) return;
    const ref = this._dialog.open<RoomDialog, RoomDialogData, RoomDialogResult>(RoomDialog, {
      panelClass: 'room-dialog',
      backdropClass: 'room-backdrop',
      data: { mode: 'create' },
    });
    ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((result) => {
        if (!result || this.loading() || this.draftRooms().length >= 50) return;
        this.draftRooms.update((rooms) => [
          ...rooms,
          {
            tempId: crypto.randomUUID(),
            name: result.roomName,
            isPrivate: result.isPrivate,
            password: result.password,
            schedule: result.schedule ?? undefined,
          },
        ]);
      });
  }

  removeRoom(tempId: string) {
    if (this.loading()) return;
    this.draftRooms.update((rooms) => rooms.filter((r) => r.tempId !== tempId));
  }

  // Human-readable schedule summary for a room tile's clock-icon tooltip:
  // the (first) occurrence window plus a repeat hint when recurring.
  scheduleTooltip(schedule: RoomSchedule | undefined): string {
    if (!schedule) return '';
    const locale = this._locale();
    const fmt = (ms: number) => formatDate(ms, SCHEDULE_DATE_FORMAT, locale);
    const window = `${fmt(schedule.startTime)} – ${fmt(schedule.endTime)}`;
    const repeat = schedule.recurrence ? recurrenceLabel(schedule.recurrence, this._translate) : '';
    return repeat ? `${window} · ${repeat}` : window;
  }

  async finalize() {
    // The loading guard keeps a repeated Enter (or double click) from firing
    // a second /v1/setup request while the first is in flight.
    if (!this.canFinish() || this.loading() || this._submitted || this.cooldown.remaining('setup'))
      return;
    this.loading.set(true);
    this.errors.set([]);
    const ownerPrivileges = {
      name: 'Owner',
      password: this.adminPassword,
      default: false,
      privileges: {
        editAppSettings: true,
        editAppPermissions: true,
        manageRooms: true,
        closeRoom: true,
        openChildRoom: true,
        closeChildRoom: true,
        muteUser: true,
        pokeUser: true,
        kickUser: true,
        grantAccess: true,
        joinAnyRoom: true,
        makeRoomPrivate: true,
        useMicrophone: true,
        useCamera: true,
        clearDrawings: true,
        clearTextChat: true,
        startRecording: true,
        grantTalkPermission: true,
        shareFiles: true,
      },
    };
    const permissions: Record<string, RoleWithPassword> = {
      // The default role carries no password — anonymous users get it.
      guest: {
        name: 'Guest',
        default: true,
        privileges: initialGuestPrivileges(),
      },
      owner: ownerPrivileges,
    };

    const draftRooms = this.draftRooms();

    // Single multipart request: uploads any selected assets and persists the
    // app config + roles + the initial rooms atomically. The response carries
    // authoritative startup state and the created rooms.
    try {
      // Each room gets its id here so its password can be proven for it; the
      // (deliberately slow) proofs derive in parallel.
      const roomsPayload: SetupRoomPayload[] = await Promise.all(
        draftRooms.map(async (room) => {
          const roomId = crypto.randomUUID();
          return {
            tempId: room.tempId,
            roomId,
            name: room.name,
            isPrivate: room.isPrivate,
            hasPassword: !!room.password,
            schedule: room.schedule,
            ...(room.password
              ? { passwordProof: await deriveRoomPasswordProof(room.password, roomId) }
              : {}),
          };
        })
      );
      const result = await this._appConfigService.syncSetup(permissions, roomsPayload);
      if ('error' in result) {
        // Surface the error and keep the wizard state (incl. the room list) so
        // the user can retry without re-entering everything.
        this.errors.set([result.message]);
        return;
      }

      // The save is committed. A later client/realtime failure must not resubmit it.
      this._submitted = true;

      // Move each e2e cipher password from its local entry onto the real roomId,
      // and surface the new rooms immediately so room-login lists them without
      // waiting for the WebSocket snapshot.
      for (const created of result.rooms) {
        const draft = draftRooms.find((r) => r.tempId === created.tempId);
        if (draft?.password) {
          this._roomPasswords.set(created.roomId, draft.password);
        }
        this._rooms.add({
          roomId: created.roomId,
          name: created.name,
          isPrivate: created.isPrivate,
          hasPassword: created.hasPassword,
          isHidden: false,
          memberCount: 0,
          schedule: created.schedule,
        });
      }

      this._appService.completeSetup(result.startup);
    } catch {
      this.errors.set(['Error setting up the app. Please try again later.']);
    } finally {
      this.loading.set(false);
    }
  }
}
